import {ApplicationConfig, importProvidersFrom} from "@angular/core";
import {BrowserModule} from "@angular/platform-browser";
import {MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule} from "@angular/material/dialog";
import {MatSelectModule} from "@angular/material/select";
import {ReactiveFormsModule} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {provideRouter, RouteReuseStrategy, RouterModule} from "@angular/router";
import {FreshRouteReuseStrategy} from "./shared/utils/fresh-route-reuse.strategy";
import {MonacoEditorModule} from "ngx-monaco-editor-v2";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions, MatTooltipModule} from "@angular/material/tooltip";
import {NgChartsModule} from "ng2-charts";
import {QuillModule} from "ngx-quill";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from "@angular/material/form-field";
import {MAT_CHIPS_DEFAULT_OPTIONS} from "@angular/material/chips";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {initializeApp, provideFirebaseApp} from "@angular/fire/app";
import {environment} from "../environments/environment";
import {getAuth, provideAuth} from "@angular/fire/auth";
import {getFirestore, provideFirestore} from "@angular/fire/firestore";
import {getFunctions, provideFunctions} from "@angular/fire/functions";
import {provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {provideAnimations} from "@angular/platform-browser/animations";
import {routes} from "./app.routes";
import {MatNativeDateModule} from "@angular/material/core";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {getStorage, provideStorage} from "@angular/fire/storage";

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatSelectModule,
      ReactiveFormsModule,
      MatInputModule,
      RouterModule,
      // Explicit absolute baseUrl (not the default relative "./assets/monaco/min/vs"): monaco's
      // JS/TS language-service worker resolves its own worker script URL with `new URL(...)` and
      // no base, so a scheme-less string fails to parse inside that worker context — this only
      // shows up for JS/TS-mode editors (e.g. the custom code transformation), not the SQL/JSON
      // ones elsewhere in the app, which don't spawn that worker.
      MonacoEditorModule.forRoot({baseUrl: `${window.location.origin}/assets/monaco/min/vs`}),
      MatAutocompleteModule,
      MatTooltipModule,
      NgChartsModule,
      QuillModule.forRoot(),
      MatNativeDateModule
    ),
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    {
      // A Material tooltip is hoverable by default — the panel keeps pointer events and the
      // component listens for its own `mouseleave`, so a tooltip that opens under the cursor stays
      // up while the mouse sits on it, after the button itself is long gone. Nothing in this app
      // puts anything interactive inside a tooltip, so the hover target is pure nuisance.
      //
      // `disableTooltipInteractivity` puts `pointer-events: none` on the panel: the mouse passes
      // straight through, and leaving the button is the only thing that decides when it hides.
      // Global by necessity — MatTooltip reads this only from the injected defaults, there is no
      // per-directive input for it.
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { showDelay: 0, hideDelay: 0, touchendHideDelay: 1500, disableTooltipInteractivity: true } as MatTooltipDefaultOptions
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic'
      }
    },
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [COMMA, ENTER]
      }
    },
    provideRouter(routes),
    { provide: RouteReuseStrategy, useClass: FreshRouteReuseStrategy },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions()),
    provideStorage(() => getStorage()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideAnimationsAsync()
  ]
};
